import moment from 'moment';
import { Text } from '../../Text';
import { memo, useMemo } from 'react';
import {
  messageStrings,
  sendAllMessageStrings,
} from '../../../constants/strings';
import { normalizePubNubTimetoken } from '../../../utils/pubnubTimetoken';
import { Icon } from '../../Icon';
import { MessageAiHistoryMenu } from '../MessageAiHistoryMenu';
import {
  CHAT_DOCUMENT_STRINGS,
  getChatFileKind,
  isChatDocumentKind,
} from '../constants';
import getFileTypeFromUrl from '../../../utils/getFileTypeFromUrl';
import { toSafeHttpUrl } from '../../../utils/safeUrl';
import type { LightboxMedia } from '../../MediaLightbox';

type MessageDetail = {
  channel: string;
  message: {
    text?: string;
    excludeFromChatList?: boolean;
    file?: unknown;
    media?: Array<{
      type?: string;
      url?: string;
      fileName?: string;
      originalHeight?: number;
      originalWidth?: number;
      poster?: string;
      actionUrl?: string;
      actionLabel?: string;
    }>;
  };
  timetoken: string | number;
  uuid?: string;
  url?: string;
  recalled?: boolean;
};

type Props = {
  message: MessageDetail;
  isMessageFromManagement?: boolean;
  enableAi?: boolean;
  selected?: boolean;
  onToggleSelect?: (timetoken: string, text: string) => void;
  onShowAiHistory?: (timetoken: string) => void;
  canDelete?: boolean;
  onDeleteRequest?: (timetoken: string) => void;
  onMediaClick?: (media: LightboxMedia) => void;
  // zip は管理側が publish したものだけ開ける（MessageList が publisher から判定して渡す）
  canOpenZip?: boolean;
};

// リスト全体がライトボックス開閉などで再レンダーされても、props が変わらない
// メッセージの再描画を避けるためメモ化する
export const MessageItem = memo(function MessageItem({
  message,
  isMessageFromManagement,
  enableAi = false,
  selected = false,
  onToggleSelect,
  onShowAiHistory,
  canDelete = false,
  onDeleteRequest,
  onMediaClick,
  canOpenZip = false,
}: Props) {
  const messageTime = moment(+String(message.timetoken) / 10000).format(
    'HH:mm',
  );
  const text = message.message?.text || '';
  const hasText = !!text.trim();
  const canSelect = enableAi && !isMessageFromManagement && hasText && !message.recalled;
  const timetoken = normalizePubNubTimetoken(message.timetoken);
  const isRecalled = message.recalled === true;
  const isRecallNotification =
    (message.message as { event?: string })?.event === 'RECALL_MESSAGE';

  const hasFile = !!message.message?.file || !!message.url;
  const media =
    message.message?.media &&
    Array.isArray(message.message.media) &&
    message.message.media.length > 0
      ? message.message.media[0]
      : null;

  const [actionUrl, actionLabel] = useMemo(
    () => [media?.actionUrl, media?.actionLabel],
    [media],
  );

  if (isRecallNotification) {
    return null;
  }
  const urlFileType =
    message.url && !media ? getFileTypeFromUrl(message.url) : undefined;
  // media.type と urlFileType は排他（urlFileType は media が無いときだけ設定される）
  const fileKind = getChatFileKind(media?.type ?? urlFileType);
  // PDF / ZIP はファイル名カードで表示する
  const documentStrings = isChatDocumentKind(fileKind)
    ? CHAT_DOCUMENT_STRINGS[fileKind]
    : undefined;
  // URL はメッセージペイロード由来で内容を保証できないため、http(s) のみ通す。
  // zip はさらに管理側が publish したもの（canOpenZip）に限って開けるようにする
  const documentUrl =
    documentStrings && (fileKind !== 'zip' || canOpenZip)
      ? toSafeHttpUrl(media?.url || message.url)
      : undefined;
  const isVideo = fileKind === 'video';
  // PubNub ファイル（hasFile）は画像。media は image/ 型のみを画像として扱う
  const isImage =
    fileKind === 'image' && (hasFile || media?.type?.startsWith('image/'));

  const recalledContentClass = isRecalled
    ? 'text-neutral-400 opacity-60 line-through'
    : 'text-neutral-600';

  const MessageMeta = (
    <div
      className={`flex ${
        !isMessageFromManagement ? 'flex-row-reverse' : ''
      } items-end mb-1 mx-3 gap-1`}
    >
      <Text textSize="text-[10px]">{messageTime}</Text>
    </div>
  );

  return (
    <div
      className={`flex break-all group/msg ${isMessageFromManagement ? 'items-end justify-end' : ''}`}
    >
      {canDelete && onDeleteRequest && !isRecalled && (
        <button
          type="button"
          onClick={() => onDeleteRequest(timetoken)}
          className={`flex items-center shrink-0 self-center mr-1 opacity-0 group-hover/msg:opacity-100 transition-opacity text-neutral-400 hover:text-red-500`}
          aria-label={messageStrings.recallMessage}
        >
          <Icon icon="close" width={14} height={14} />
        </button>
      )}
      {canSelect && (
        <div
          className={`flex items-center shrink-0 self-center mr-1 ${
            selected ? 'opacity-100' : 'opacity-0 group-hover/msg:opacity-100'
          } transition-opacity`}
        >
          <button
            type="button"
            onClick={() => onToggleSelect?.(timetoken, text)}
            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
              selected
                ? 'border-blue-500 bg-blue-500'
                : 'border-neutral-300 hover:border-blue-400'
            }`}
          >
            {selected && (
              <Icon
                icon="checkBold"
                width={12}
                height={12}
                className="text-white"
              />
            )}
          </button>
        </div>
      )}
      {isMessageFromManagement && MessageMeta}

      <div className={`max-w-[75%] md:max-w-[55%] lg:max-w-[40%] ${isMessageFromManagement ? '' : 'ml-3'}`}>
        <div className="flex">
          <div
            className={`${
              isMessageFromManagement ? 'bg-primary-200' : 'bg-transparent'
            } rounded-xl border border-neutral-200 flex-1`}
          >
            {isRecalled && (
              <div className="px-3 pt-2 text-[11px] text-neutral-400">
                {messageStrings.recalledMessageLabel}
              </div>
            )}
            {text && (
              <div
                className={`px-3 py-2 whitespace-pre-line text-normal ${recalledContentClass}`}
              >
                {text}
              </div>
            )}
            {documentStrings &&
              (documentUrl ? (
                <button
                  type="button"
                  onClick={() => {
                    if (!isRecalled) {
                      window.open(documentUrl, '_blank', 'noopener,noreferrer');
                    }
                  }}
                  className={`flex items-center gap-2 px-3 py-2 text-left ${recalledContentClass} ${
                    isRecalled ? 'cursor-default' : 'hover:opacity-80'
                  }`}
                >
                  <Icon
                    icon="attach"
                    width={20}
                    height={20}
                    className="shrink-0"
                  />
                  <span className="underline break-all">
                    {media?.fileName || documentStrings.fileName}
                  </span>
                </button>
              ) : (
                // URL が http(s) でない／開けない zip の場合は開かせないが、無言で消すと気づけないため理由を出す
                <div className="flex items-center gap-2 px-3 py-2 text-neutral-400 text-sm">
                  <Icon
                    icon="attach"
                    width={20}
                    height={20}
                    className="shrink-0"
                  />
                  <span className="break-all">
                    {documentStrings.unavailable}
                  </span>
                </div>
              ))}
            {isImage && (message.url || media?.url) && (
              <button
                type="button"
                disabled={isRecalled || (!actionUrl && !onMediaClick)}
                aria-label={actionUrl ? 'リンクを開く' : '画像を拡大表示'}
                className={`block w-full text-left ${recalledContentClass} ${
                  !isRecalled && (actionUrl || onMediaClick)
                    ? 'cursor-pointer hover:opacity-90'
                    : ''
                }`}
                onClick={() => {
                  if (isRecalled) return;
                  if (actionUrl) {
                    window.open(actionUrl, '_blank', 'noopener,noreferrer');
                  } else {
                    const imageUrl = message.url || media?.url;
                    if (imageUrl) {
                      onMediaClick?.({ url: imageUrl });
                    }
                  }
                }}
              >
                <img
                  src={message.url || media?.url}
                  alt=""
                  data-testid="message-image"
                  className={`max-w-full rounded-lg ${text ? 'mb-2' : ''} ${
                    isRecalled ? 'opacity-60' : ''
                  }`}
                />
              </button>
            )}
            {isVideo && (media?.url || message.url) && (
              <div className={`relative ${isRecalled ? 'opacity-60' : ''}`}>
                <video
                  src={media?.url || message.url}
                  poster={media?.poster || undefined}
                  controls={!isRecalled}
                  data-testid="message-video"
                  className={`max-w-full rounded-lg ${text ? 'mb-2' : ''}`}
                  style={{ maxHeight: '400px' }}
                />
                {actionUrl && !isRecalled && (
                  <div className="absolute right-1 flex justify-center px-0 z-10 top-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(actionUrl, '_blank', 'noopener,noreferrer');
                      }}
                      className="bg-black bg-opacity-40 text-white text-base px-3 py-1 rounded-3xl font-medium shadow-lg text-center whitespace-nowrap hover:bg-opacity-50 transition-opacity max-w-[200px] truncate"
                      title={
                        actionLabel?.trim() ||
                        sendAllMessageStrings.actionLabelDefault
                      }
                    >
                      {actionLabel?.trim() ||
                        sendAllMessageStrings.actionLabelDefault}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {canSelect && onShowAiHistory && !isMessageFromManagement && (
            <MessageAiHistoryMenu
              timetoken={timetoken}
              onShowHistory={onShowAiHistory}
            />
          )}

          {!isMessageFromManagement && MessageMeta}
        </div>
      </div>
    </div>
  );
});
